/*
 * landing-params.js: carry each visitor's landing data onto the order.
 *
 * On every page load it reads utm_*, click IDs, the referrer and the landing
 * path+query, keeps a first touch (set once) and a last touch (replaced on a
 * new landing), and copies both into cart attributes, which Shopify turns into
 * the order's note attributes. Values pass through exactly as the URL carries
 * them (URLSearchParams decoding only); the raw query is kept verbatim in
 * *_landing_page, minus parameters that can carry a credential (SENSITIVE).
 * Every step swallows its own errors so nothing here can break the page or
 * block checkout.
 */
(function () {
  var KEY = 'almaza_lp_';
  var CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'epik', '_kx'];
  // What the cookie fallback keeps when a touch is over the cookie limit.
  var CORE = ['utm_source', 'utm_medium', 'utm_campaign', 'timestamp'].concat(CLICK_IDS);
  // The store's other hosts, Shopify checkout and Shop Pay, and payment returns:
  // coming back from these is the same visit, not a new landing.
  var INTERNAL = /(^|\.)(almazafinejewelry\.com|myshopify\.com|shopify\.com|shop\.app|paypal\.com|pay\.google\.com|klarna\.com|afterpay\.com|affirm\.com)$/i;
  // Account links (password reset, activation) carry one-time tokens in the
  // path or query; never copy those onto the order. Optional locale prefix
  // with any number of subtags, such as /es-419/, /en-us/ or /zh-Hant-TW/.
  var PRIVATE_PATH = /^\/([a-z]{2,3}(-[a-z0-9]{1,8})*\/)?(account|challenge|password)(\/|$)/i;
  var ADD = /\/cart\/add(\.js)?\/?$/;
  // Parameters that can carry a credential or personal data. They are dropped
  // from the stored landing page and referrer so they never reach the order.
  var SENSITIVE = /^(token|access_token|id_token|refresh_token|auth|auth_token|authorization|sig|signature|hmac|secret|password|passwd|apikey|api_key|client_secret|jwt|bearer|customer_id|session_id|sessionid|key|return_to|redirect|_auth|checkout_url|email|phone)$/i;
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var nativeFetch = window.fetch && window.fetch.bind(window);
  if (!nativeFetch) return;
  var ft, lt;

  function noop() {}

  // A touch is only usable with a timestamp; anything else counts as missing.
  function valid(t) {
    return !!t && typeof t === 'object' && typeof t.timestamp === 'string' && t.timestamp ? t : null;
  }

  // A touch read back from storage or the cart may predate SENSITIVE: scrub
  // its URLs again so an old secret is never copied on.
  function clean(t) {
    if (t) ['landing_page', 'referrer'].forEach(function (k) { if (typeof t[k] === 'string') t[k] = scrub(t[k]); });
    return t;
  }

  // The newer of two touches; a tie keeps the first.
  function newer(a, b) {
    return b && (!a || b.timestamp > a.timestamp) ? b : a;
  }

  // Each touch is stored on its own (localStorage, else a cookie) so each
  // gets the whole cookie limit.
  function get(name) {
    var raw = null;
    try {
      raw = localStorage.getItem(KEY + name);
    } catch (e) {}
    try {
      var m = raw === null && document.cookie.match(new RegExp('(?:^|; )' + KEY + name + '=([^;]*)'));
      if (m) raw = decodeURIComponent(m[1]);
      return clean(valid(JSON.parse(raw)));
    } catch (e) {
      return null;
    }
  }

  function put(name, touch) {
    if (!touch) return;
    try {
      localStorage.setItem(KEY + name, JSON.stringify(touch));
      return;
    } catch (e) {}
    // A cookie holds about 4 KB: store the whole touch, else its core fields.
    var core = {};
    CORE.forEach(function (k) { if (touch[k]) core[k] = touch[k]; });
    [touch, core].some(function (form) {
      var v = encodeURIComponent(JSON.stringify(form));
      if (v.length >= 4000) return false;
      try {
        document.cookie = KEY + name + '=' + v + '; path=/; max-age=31536000; SameSite=Lax';
      } catch (e) {}
      return true;
    });
  }

  function internal(host) {
    return host.replace(/^www\./, '') === location.hostname.replace(/^www\./, '') || INTERNAL.test(host);
  }

  // The '&'- or ';'-separated parameters after `mark` ('?' or '#') minus the
  // sensitive ones (token[] and token[0] count as token), each kept raw and in
  // order with the separator before it; '' (no mark) when none are left.
  function keep(mark, part) {
    if (part === null) return '';
    var bits = part.split(/([&;])/);
    var kept = [];
    for (var i = 0; i < bits.length; i += 2) {
      var key = '';
      new URLSearchParams(bits[i]).forEach(function (v, k) { key = k; });
      if (!SENSITIVE.test(key.replace(/^\?+/, '').replace(/\[.*$/, ''))) kept.push((kept.length ? bits[i - 1] : '') + bits[i]);
    }
    if (kept.length * 2 - 1 === bits.length) return mark + part;
    return kept.join('') ? mark + kept.join('') : '';
  }

  // The URL with sensitive parameters dropped from its query and fragment.
  function scrub(url) {
    var hash = url.indexOf('#');
    var head = hash > -1 ? url.slice(0, hash) : url;
    var q = head.indexOf('?');
    return (q > -1 ? head.slice(0, q) : head) + keep('?', q > -1 ? head.slice(q + 1) : null) + keep('#', hash > -1 ? url.slice(hash + 1) : null);
  }

  // A reload or back/forward shows an old tab's URL again; it is not a new landing.
  function revisit() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      var type = nav ? nav.type : ['navigate', 'reload', 'back_forward'][performance.navigation.type];
      return type === 'reload' || type === 'back_forward';
    } catch (e) {
      return false;
    }
  }

  function currentTouch() {
    var touch = {};
    var isLanding = false;
    new URLSearchParams(location.search).forEach(function (value, key) {
      if ((key.indexOf('utm_') === 0 || CLICK_IDS.indexOf(key) > -1) && !(key in touch)) {
        touch[key] = value;
        if (value) isLanding = true;
      }
    });
    if (!PRIVATE_PATH.test(location.pathname)) touch.landing_page = scrub(location.pathname + location.search);
    try {
      // Same-site referrers say nothing about the source and can be account pages.
      if (document.referrer && !internal(new URL(document.referrer).hostname)) {
        touch.referrer = scrub(document.referrer);
        isLanding = true;
      }
    } catch (e) {}
    touch.timestamp = new Date().toISOString();
    return { touch: touch, isLanding: isLanding && !revisit() };
  }

  // The cart's ft_* or lt_* attributes as a touch.
  function fromCart(attrs, prefix) {
    var touch = {};
    Object.keys(attrs).forEach(function (name) {
      if (name.indexOf(prefix + '_') === 0) touch[name.slice(3)] = attrs[name];
    });
    return clean(valid(touch));
  }

  function copy(out, prefix, touch) {
    Object.keys(touch).forEach(function (k) {
      if (typeof touch[k] === 'string' && touch[k]) out[prefix + '_' + k] = touch[k];
    });
  }

  // The cart's attributes as last read or written. Until the first read
  // returns it holds the stored last touch, the best guess at what the cart has.
  var attrs = {};
  var cartRead = false;

  function send(out) {
    if (!Object.keys(out).length) return;
    Object.keys(out).forEach(function (n) { if (out[n]) attrs[n] = out[n]; else delete attrs[n]; });
    var url = root + 'cart/update.js';
    var init = {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: out })
    };
    // keepalive lets the write finish when the shopper leaves for checkout, but
    // browsers refuse keepalive bodies over 64 KB, so large ones go plain.
    nativeFetch(url, Object.assign({ keepalive: new Blob([init.body]).size < 60000 }, init))
      .catch(function () { return nativeFetch(url, init); })
      .catch(noop);
  }

  // Write what the cart lacks without waiting for a read, so a quick click to
  // checkout can't outrun it. The cart is the truth once it holds a touch:
  // storage may have been lost (Safari expires it) or blocked. Its first touch
  // is never replaced; its last touch only by a newer landing, so an older tab
  // can't roll it back.
  function write() {
    try {
      var cartFt = fromCart(attrs, 'ft');
      var cartLt = fromCart(attrs, 'lt');
      ft = cartFt || ft;
      // Another tab may have stored a newer landing since this page loaded.
      lt = newer(lt, get('lt'));
      if (!lt || (cartLt && cartLt.timestamp > lt.timestamp)) lt = cartLt;
      put('ft', ft);
      put('lt', lt);
      var out = {};
      // A first touch made on this page may stand in for one lost from storage,
      // so it is sent only once a read shows the cart has none. A cart's own
      // first touch only has its scrubbed URLs sent back.
      if (cartRead) {
        var first = {};
        copy(first, 'ft', ft);
        Object.keys(first).forEach(function (n) { if (!cartFt || attrs[n] !== first[n]) out[n] = first[n]; });
      }
      if (lt) {
        var want = {};
        copy(want, 'lt', lt);
        // Shopify deletes an attribute sent as "": clear what the touch lacks.
        Object.keys(attrs).forEach(function (n) { if (n.indexOf('lt_') === 0 && !(n in want)) want[n] = ''; });
        if (Object.keys(want).some(function (n) { return (attrs[n] || '') !== want[n]; })) Object.assign(out, want);
      }
      send(out);
    } catch (e) {}
  }

  function sync() {
    return nativeFetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        attrs = cart.attributes || {};
        cartRead = true;
        write();
      })
      .catch(noop);
  }

  function added() {
    write();
    sync();
  }

  // Only a landing sets the last touch; any other page view keeps what is
  // stored or, when storage is empty, what the cart already has.
  try {
    var now = currentTouch();
    var stored = get('lt');
    ft = get('ft') || now.touch;
    lt = now.isLanding ? now.touch : stored;
    if (stored) copy(attrs, 'lt', stored);
  } catch (e) {
    return;
  }

  function isAdd(url) {
    try {
      return ADD.test(new URL(url, document.baseURI).pathname);
    } catch (e) {
      return false;
    }
  }

  // Dawn's product form, quick add and most apps add to cart with fetch; some
  // apps use XHR. Watch both rather than Dawn's pubsub event (only the theme fires it).
  window.fetch = function (input) {
    var p = nativeFetch.apply(null, arguments);
    try {
      if (isAdd(input && input.url ? input.url : input)) {
        p.then(function (r) { if (r.ok) added(); }, noop);
      }
    } catch (e) {}
    return p;
  };

  try {
    var xhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (isAdd(url)) {
          this.addEventListener('load', function () { if (this.status >= 200 && this.status < 300) added(); }, { once: true });
        }
      } catch (e) {}
      return xhrOpen.apply(this, arguments);
    };
  } catch (e) {}

  // A landing reaches the cart now, even an empty one, before any add.
  write();
  sync();
})();
